import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import {
  listProjects,
  loadSavedProject,
  purgeEphemeralProjects,
  type ProjectMeta,
} from "../model/storage.ts";
import { driveSyncStore } from "../model/driveSyncStore.ts";

/**
 * "Your projects" (the list, not any one open project) plus its Google
 * Drive reconciliation — split out of useProjectPersistence.ts (which had
 * grown to ~840 lines bundling this together with the single open
 * project's own open/save/rename/export/delete/autosave lifecycle, see
 * project_evalhid_code_audit.md's ongoing "large file" pass). The only
 * thing the rest of useProjectPersistence.ts still needs from here is
 * `reloadSavedProjects` itself — every mutating operation there (save,
 * rename, delete, close...) calls it once done, to keep "Your projects"
 * in sync — threaded through as a plain stable callback (`useCallback`
 * with an empty dependency array, same as before this split), the normal
 * way one hook's result feeds another's effects.
 */
export function useSavedProjectsList() {
  const [savedProjects, setSavedProjects] = useState<ProjectMeta[]>([]);
  const reloadSavedProjects = useCallback(() => {
    listProjects()
      .then(setSavedProjects)
      .catch((err) => console.error("Could not list saved projects:", err));
  }, []);
  /** Local projects with no Drive copy yet, found by the reconcile effect below — offer to upload them (see UploadExistingProjectsDialog.tsx) rather than doing it silently. `null`: nothing to ask about (not connected, or every local project already has a Drive copy). */
  const [driveUnsyncedProjects, setDriveUnsyncedProjects] = useState<ProjectMeta[] | null>(null);
  // Examples autosave like any other project (same flow, see
  // finishLoadingProject) but marked `ephemeral` — they don't count as
  // "really saved", so they're cleared once on startup instead of piling
  // up in IndexedDB session after session. Also tries a silent Drive
  // reconnect (only actually attempts anything if the user connected in a
  // previous session — see driveSyncStore.ts); the reconcile itself lives in
  // its own effect below, since it has to run again on a manual "Connect"
  // too, not just this mount-time attempt.
  useEffect(() => {
    (async () => {
      try {
        await purgeEphemeralProjects();
      } catch (err) {
        console.error("Could not clean up previous example projects:", err);
      }
      const localList = await listProjects().catch((err) => {
        console.error("Could not list saved projects:", err);
        return [] as ProjectMeta[];
      });
      setSavedProjects(localList);
      // A still-valid cached token (see driveSyncStore.ts's
      // tryReuseStoredToken) skips Google entirely — only once that comes
      // back empty (nothing cached, or it's actually expired) does this
      // fall back to the silent reconnect attempt that can end up needing
      // (and possibly not getting) a popup.
      if (!driveSyncStore.tryReuseStoredToken()) {
        await driveSyncStore.trySilentReconnect();
      }
    })();
    // Deliberately runs once at mount only — reconciliation itself is the
    // effect below, driven by `driveConnected` instead of this mount tick.
  }, []);

  const driveConnected = useSyncExternalStore(
    driveSyncStore.subscribe,
    () => driveSyncStore.getSnapshot().connected
  );
  /**
   * Reconciles against Drive on every fresh connection — the silent
   * reconnect above AND a manual "Connect" from Settings both end up
   * flipping `driveConnected` to `true`, and both need this: a user who
   * connects for the first time well after the app has already loaded
   * (the common case — Drive isn't set up on first launch) still has local
   * projects that have never been offered for upload otherwise. May pull
   * down Drive-only projects (reloadSavedProjects picks those up) and/or
   * surface local projects Drive doesn't know about yet.
   */
  useEffect(() => {
    if (!driveConnected) return;
    (async () => {
      const localList = await listProjects().catch((err) => {
        console.error("Could not list saved projects:", err);
        return [] as ProjectMeta[];
      });
      try {
        const { unsyncedLocal } = await driveSyncStore.reconcile(localList);
        if (unsyncedLocal.length > 0) setDriveUnsyncedProjects(unsyncedLocal);
        reloadSavedProjects();
      } catch (err) {
        console.error("Could not reconcile with Google Drive:", err);
      }
    })();
  }, [driveConnected, reloadSavedProjects]);

  /** "Upload all" from UploadExistingProjectsDialog.tsx — queues each of `driveUnsyncedProjects` (loading its full data first, same as any other queueSync call) and dismisses the dialog. */
  const uploadUnsyncedToDrive = useCallback(() => {
    const pending = driveUnsyncedProjects ?? [];
    setDriveUnsyncedProjects(null);
    for (const meta of pending) {
      loadSavedProject(meta.id)
        .then((data) => {
          if (data) driveSyncStore.queueSync(meta, data);
        })
        .catch((err) => console.error(`Could not load "${meta.name}" to sync it to Drive:`, err));
    }
  }, [driveUnsyncedProjects]);

  const dismissUnsyncedDriveDialog = useCallback(() => setDriveUnsyncedProjects(null), []);

  /** "Use the Drive version" for a project flagged as a sync conflict (Landing.tsx) — re-downloads and overwrites the local copy, then refreshes "Your projects" (new name/date). */
  const resolveDriveConflict = useCallback(
    (projectId: string) => {
      driveSyncStore
        .resolveConflict(projectId)
        .then(reloadSavedProjects)
        .catch((err) =>
          console.error(`Could not resolve the Drive conflict for project ${projectId}:`, err)
        );
    },
    [reloadSavedProjects]
  );

  return {
    savedProjects,
    reloadSavedProjects,
    driveUnsyncedProjects,
    uploadUnsyncedToDrive,
    dismissUnsyncedDriveDialog,
    resolveDriveConflict,
  };
}
